import { FC, Fragment, useCallback } from 'react';
import { Add as AddIcon, Delete } from '@mui/icons-material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Card, FormHelperText, Grid, IconButton, Link, Typography } from '@mui/material';
// components
import InputController from 'controller/InputController';
import SelectController from 'controller/Select';
import { InfoTooltip } from 'components/common/InfoTooltip';
// constants, styles
import {
  ADD_TEXT,
  AGE_GROUPS_TEXT,
  AGE_GROUP_TYPE_MAPPED,
  AGE_GROUP_TYPE_PRIMARY_TOOLTIP,
  AGE_GROUP_TYPE_TEXT,
  DV_CONSTRAINTS_TYPE_MAPPED,
  DV_CONSTRAINTS_TYPE_TEXT,
  formulaAgeGroupInitialValue,
  SERVING_PER_CONTAINER,
  SERVING_SIZE_TEXT,
  SERVING_UNIT_MAPPED,
  SERVING_UNIT_TEXT,
} from 'constants/index';
import { DosageFormulationFormType } from 'interfaces';
import { cardContentBoxSx, displayFlexAlignCenter } from 'theme/styleConstant';
import { forInputLabelBg } from 'styles/commonComponentStyle';

const FormulaAgeGroupCard: FC = () => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<DosageFormulationFormType>();

  const { fields, append, remove } = useFieldArray({
    name: 'ageGroups',
    control,
  });

  const ageGroups = watch('ageGroups');

  const allTypesAdded = fields?.length >= DV_CONSTRAINTS_TYPE_MAPPED?.length;

  // Get filtered DV options for a specific row index (exclude types selected in other rows)
  const getFilteredOptions = useCallback(
    (currentIndex: number) => {
      const selectedTypes =
        ageGroups?.map((group, idx) => (idx !== currentIndex ? group?.dvType : null)).filter(Boolean) || [];
      const currentValue = ageGroups?.[currentIndex]?.dvType;
      return DV_CONSTRAINTS_TYPE_MAPPED.filter(
        (option) => !selectedTypes.includes(option.value) || option.value === currentValue,
      );
    },
    [ageGroups],
  );

  // "Primary" can only be selected once; "Other" can be selected multiple times.
  const getFilteredAgeGroupOptions = useCallback(
    (currentIndex: number) => {
      const selectedTypes =
        ageGroups?.map((group, idx) => (idx !== currentIndex ? group?.ageGroupType : null)).filter(Boolean) ||
        [];
      const currentValue = ageGroups?.[currentIndex]?.ageGroupType;
      return AGE_GROUP_TYPE_MAPPED.filter((option) => {
        if (option.value === 'OTHER') return true;
        return !selectedTypes.includes(option.value) || option.value === currentValue;
      });
    },
    [ageGroups],
  );

  const handleAddItem = useCallback(() => {
    append(formulaAgeGroupInitialValue);
  }, [append]);

  const handleRemoveEntry = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  const ageGroupsError = errors?.ageGroups as { message?: string } | undefined;

  return (
    <Card sx={forInputLabelBg}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {AGE_GROUPS_TEXT}
      </Typography>
      {ageGroupsError?.message && (
        <FormHelperText error sx={{ mb: 1 }}>
          {ageGroupsError.message}
        </FormHelperText>
      )}

      {/* Display added entries with editable fields */}
      {fields?.map((item, index) => {
        return (
          <Fragment key={item.id}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Box sx={cardContentBoxSx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} xl={6}>
                    <SelectController
                      isRequired
                      name={`ageGroups.${index}.dvType`}
                      title={DV_CONSTRAINTS_TYPE_TEXT}
                      options={getFilteredOptions(index)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} xl={6}>
                    <InputController
                      isRequired
                      fieldType="number"
                      name={`ageGroups.${index}.servingSize`}
                      title={SERVING_SIZE_TEXT}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} xl={6}>
                    <SelectController
                      isRequired
                      name={`ageGroups.${index}.servingUnit`}
                      title={SERVING_UNIT_TEXT}
                      options={SERVING_UNIT_MAPPED}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} xl={6}>
                    <InputController
                      isRequired
                      fieldType="number"
                      name={`ageGroups.${index}.servingContainer`}
                      title={SERVING_PER_CONTAINER}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} xl={6}>
                    <SelectController
                      isRequired
                      name={`ageGroups.${index}.ageGroupType`}
                      title={AGE_GROUP_TYPE_TEXT}
                      options={getFilteredAgeGroupOptions(index)}
                      endAdornment={<InfoTooltip title={AGE_GROUP_TYPE_PRIMARY_TOOLTIP} />}
                    />
                  </Grid>
                </Grid>
              </Box>
              {/* Show delete button for all entries when there are 2 or more entries */}
              {fields.length > 1 && (
                <IconButton
                  size="medium"
                  color="error"
                  onClick={() => handleRemoveEntry(index)}
                  aria-label="delete">
                  <Delete fontSize="medium" />
                </IconButton>
              )}
            </Box>
          </Fragment>
        );
      })}

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
        <Link
          component="button"
          type="button"
          disabled={allTypesAdded}
          onClick={handleAddItem}
          sx={[
            displayFlexAlignCenter,
            allTypesAdded
              ? { color: 'customGrayColor.light', cursor: 'not-allowed' }
              : { color: 'primary.main' },
          ]}>
          <AddIcon />
          <Typography variant="caption" pl={0.5} fontWeight={600}>
            {ADD_TEXT}
          </Typography>
        </Link>
      </Box>
    </Card>
  );
};

export default FormulaAgeGroupCard;
